import React, { useCallback, useEffect, useState } from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import repairOrdersService from '../../../services/repairOrders.service';
import { RepairOrderCard, RepairOrderPingType } from '../../../types/repairOrders';
import { useToast } from '../../../contexts/ToastContext';
import RepairOrderCardList from '../../../components/RepairOrderCardList';
import RepairOrderDrawer from '../../fleetmanagement/components/RepairOrderDrawer';

export interface RepairOrdersPanelProps {
  /** Pro number for the order. List/refetch is gated on this being defined. */
  pronumber: number | undefined;
  /** Notifies the parent of the live count for the tab badge. */
  /** Total cards shown, for the ROs tab badge. */
  onCountChange?: (count: number) => void;
  /** Open (OPEN/PENDING/DEFERRED/DISPATCHED) subset, for the tab's red flag.
   *  Reported separately because the panel also lists the pro's CLOSED repair
   *  orders, so the badge and the flag are different numbers. */
  onOpenCountChange?: (count: number) => void;
}

/**
 * Repair Orders list panel for the Pro Details "ROs" sub-tab. Small clickable
 * cards (status, reported-by, part, latest stage date) that open the shared
 * RepairOrderDrawer by recno — same view/edit behaviour as the
 * fleet-management Repair Orders page (viewing is read-access; saving is
 * write-gated by the backend), since it's the very same drawer.
 */
const RepairOrdersPanel: React.FC<RepairOrdersPanelProps> = ({ pronumber, onCountChange, onOpenCountChange }) => {
  const [rows, setRows] = useState<RepairOrderCard[]>([]);
  // Start in the loading state when there's a pro to fetch, so the spinner
  // shows immediately on first mount instead of the "No Repair Orders" empty
  // state flashing for a frame before the fetch effect runs.
  const [loading, setLoading] = useState<boolean>(() => pronumber != null);
  const [error, setError] = useState<string | null>(null);
  const [openRecno, setOpenRecno] = useState<number | null>(null);
  const { showToast } = useToast();

  const fetchRows = useCallback(async (signal?: AbortSignal) => {
    if (!pronumber) {
      setRows([]);
      onCountChange?.(0);
      onOpenCountChange?.(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await repairOrdersService.getByPro(pronumber, signal);
      setRows(res.data);
      onCountChange?.(res.data.length);
      onOpenCountChange?.(res.openCount ?? 0);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch repair orders:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load repair orders');
    } finally {
      // Only the fetch that is still current may clear the spinner. A superseded
      // one still runs its `finally` after aborting, and without this guard it
      // turned the spinner off while its replacement was in flight — dropping
      // the panel to "No Repair Orders" for the rest of the real load, which
      // reads as "this order has none". Same guard the Trouble Tickets panel uses.
      if (!signal?.aborted) setLoading(false);
    }
  }, [pronumber, onCountChange, onOpenCountChange]);

  /**
   * Ask maintenance for an ETA / a completion status on one repair order —
   * the whole point of this task: one click here instead of an email.
   *
   * Optimistic: a synthetic ping goes into that card's openPings immediately so
   * the amber chip appears at once, then it is swapped for the server's ping on
   * success, or removed (plus an error toast) on failure. The POST is
   * idempotent server-side, so a double-click costs nothing.
   */
  const handleRequestPing = useCallback(async (recno: number, requestType: RepairOrderPingType) => {
    // Negative, monotonic placeholder id — can never collide with a real recno.
    const tempRecno = -Date.now();
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    // NAIVE local-time ISO, matching the backend's format — see RepairOrderPing.
    const requestedAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    setRows((prev) => prev.map((r) => (
      r.recno === recno
        ? { ...r, openPings: [...(r.openPings || []), { recno: tempRecno, requestType, requestedBy: 'you', requestedAt }] }
        : r
    )));

    try {
      const res = await repairOrdersService.requestPing(recno, requestType);
      setRows((prev) => prev.map((r) => (
        r.recno === recno
          ? { ...r, openPings: (r.openPings || []).map((p) => (p.recno === tempRecno ? res.ping : p)) }
          : r
      )));
    } catch (err) {
      setRows((prev) => prev.map((r) => (
        r.recno === recno
          ? { ...r, openPings: (r.openPings || []).filter((p) => p.recno !== tempRecno) }
          : r
      )));
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = e?.response?.data?.detail;
      showToast({
        type: 'error',
        message: `Ping failed: ${typeof detail === 'string' ? detail : e?.message || 'Request failed'}`,
      });
    }
  }, [showToast]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchRows(controller.signal);
    return () => controller.abort();
  }, [fetchRows]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 mb-2">
        <WrenchScrewdriverIcon className="w-4 h-4 text-slate-600 dark:text-gray-300" />
        <span className="text-sm font-semibold text-body">Repair Orders</span>
        {rows.length > 0 && (
          <span className="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-300">
            {rows.length}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <RepairOrderCardList
          rows={rows}
          loading={loading}
          error={error}
          onCardClick={setOpenRecno}
          allowPings
          onRequestPing={(recno, requestType) => { void handleRequestPing(recno, requestType); }}
        />
      </div>

      <RepairOrderDrawer
        isOpen={openRecno !== null}
        onClose={() => setOpenRecno(null)}
        orderRecno={openRecno}
        onSaved={() => { void fetchRows(); }}
      />
    </div>
  );
};

export default RepairOrdersPanel;
